'use client';

import { useWallet } from '@/api/queries/coins.queries';
import { canSeePaidDms } from '@/shared/lib/auth/permissions';
import { openCrispChat } from '@/shared/lib/crisp';
import { useAuthStore } from '@/shared/stores/auth-store';
import { Coins, CreditCard, LifeBuoy, LogOut, Settings, Shield } from 'lucide-react';
import Link from 'next/link';
import { CreatorIdentity } from './CreatorIdentity';
import { UserIdentity } from './UserIdentity';

// TEMP tester gate (FRONT-307 rollout). Balance renders only above zero —
// a 0 (or still-loading) balance leaves the plain Wallet row (design decision,
// FRONT-322 handoff §2.6). Remove with canSeePaidDms at full release.
function WalletMenuItem({ onItemClick }: { onItemClick: () => void }) {
  const user = useAuthStore((s) => s.user);
  const { data } = useWallet();
  if (!canSeePaidDms(user)) return null;

  const balance = data?.balance ?? 0;
  return (
    <Link
      href="/account/wallet"
      onClick={onItemClick}
      className="mx-2 flex items-center gap-3 rounded px-2 py-2 text-sm hover:bg-white/10 transition-colors"
    >
      <Coins className="w-4 h-4" />
      Wallet
      {balance > 0 && (
        <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-text-secondary tabular-nums">
          <Coins className="w-3.5 h-3.5 text-red-primary" />
          {balance}
        </span>
      )}
    </Link>
  );
}

function IdentityBlock({ onClose }: { onClose?: () => void }) {
  const user = useAuthStore((s) => s.user);
  if (!user) return null;

  const closeProps = onClose !== undefined ? { onClose } : {};
  const isCreator = user.is_creator ?? false;

  if (isCreator) {
    return <CreatorIdentity user={user} {...closeProps} />;
  }

  return <UserIdentity user={user} {...closeProps} />;
}

export function UserMenuContent({ onClose }: { onClose?: () => void }) {
  const logout = useAuthStore((s) => s.logout);

  const handleLogout = async () => {
    onClose?.();
    await logout();
  };

  const handleItemClick = () => {
    onClose?.();
  };

  return (
    <div className="flex flex-col">
      <IdentityBlock {...(onClose !== undefined ? { onClose } : {})} />

      {/* Account management */}
      <Link
        href="/account"
        onClick={handleItemClick}
        className="mx-2 flex items-center gap-3 rounded px-2 py-2 text-sm hover:bg-white/10 transition-colors"
      >
        <Settings className="w-4 h-4" />
        Account
      </Link>
      <Link
        href="/account/subscription"
        onClick={handleItemClick}
        className="mx-2 flex items-center gap-3 rounded px-2 py-2 text-sm hover:bg-white/10 transition-colors"
      >
        <CreditCard className="w-4 h-4" />
        Subscription
      </Link>
      <WalletMenuItem onItemClick={handleItemClick} />
      <Link
        href="/account/security"
        onClick={handleItemClick}
        className="mx-2 flex items-center gap-3 rounded px-2 py-2 text-sm hover:bg-white/10 transition-colors"
      >
        <Shield className="w-4 h-4" />
        Security
      </Link>

      <button
        type="button"
        onClick={() => {
          onClose?.();
          openCrispChat('user_menu', {}, { isAuthenticated: true });
        }}
        className="mx-2 flex items-center gap-3 rounded px-2 py-2 text-sm hover:bg-white/10 transition-colors cursor-pointer text-left"
      >
        <LifeBuoy className="w-4 h-4" />
        Help
      </button>

      <div className="h-px bg-white/10 -mx-1 my-1" />

      {/* Sign out */}
      <button
        onClick={handleLogout}
        className="mx-2 mb-1 flex items-center gap-3 rounded px-2 py-2 text-sm text-red-500 hover:bg-red-500/10 transition-colors cursor-pointer"
      >
        <LogOut className="w-4 h-4" />
        Sign out
      </button>
    </div>
  );
}
